// The contract-endpoint error vocabulary: backend `code` → i18n key.
//
// MOVED here out of `customerSwitchPayloads.ts` (which now lives at
// `components/management/customerSwitch/`) because it has TWO consumers that must not
// diverge:
//   1. CustomerSwitchModal (Manage tab, authenticated admin)
//   2. SignCommodityContractPage (PUBLIC token sign surface, company B's signatory)
// The sign page is public and must not import from a feature module at all, and
// duplicating the map would put two copies of the same contract in the tree — the exact
// drift this file exists to prevent. `customerSwitchPayloads` re-exports it, so every
// existing import (and the completeness test that pins it) keeps working unchanged.
//
// 🔴 Keys carry an EXPLICIT `management:` namespace prefix, and must keep it.
// The 61-key `customerSwitch.*` copy block lives in the `management` namespace (only
// the Manage tab reads it), but this map is resolved with the CALLER's `t` — and two of
// the three callers are bound to `addservice`: DigitalSignatureModal (onboarding) and
// SignCommodityContractPage (the PUBLIC token sign page). An unqualified key would
// resolve against their namespace, miss, and render the raw key string to a customer,
// because `li8n.ts` sets no `fallbackNS`. The prefix makes resolution independent of
// which namespace the caller's `t` happens to be bound to.

/**
 * Every backend code these endpoints can serialise, mapped to localised copy.
 *
 * An unmapped code falls through to the caller's raw-English branch — which is how an
 * NL/FR admin ended up reading a sentence containing an internal contracts_flex.id.
 *
 * 🔴 The names mostly say CUSTOMER_SWITCH_ but the map is NOT prefix-scoped, and must not
 * become so. `contractOverview`, `addContract` and the sign paths also serialise
 * `ContractPrerequisiteError` (yuso-portals-backend src/functions/contractErrors.ts) and
 * the group-validation error onto the SAME `{ error, code }` envelope, and those codes
 * carry no prefix. They were absent here for exactly that reason and their raw English
 * sentences — one of which embeds the bare EAN — were reaching admins as the headline.
 * Any code any of these endpoints can serialise belongs in this map.
 */
export const CUSTOMER_SWITCH_ERROR_KEYS: Record<string, string> = {
  CUSTOMER_SWITCH_ADMIN_ONLY: "management:customerSwitch.errorAdminOnly",
  CUSTOMER_SWITCH_MULTIPLE_OWNERS: "management:customerSwitch.errorMultipleOwners",
  CUSTOMER_SWITCH_COMBINED_ONLY_PARTIAL: "management:customerSwitch.blockedOnlyCombined",
  CUSTOMER_SWITCH_DIRECTION_MISMATCH: "management:customerSwitch.errorDirectionMismatch",
  CUSTOMER_SWITCH_FLEX_SIBLING_EAN: "management:customerSwitch.errorFlexSiblingEan",
  CUSTOMER_SWITCH_SERVICE_COMPONENTS_UNKNOWN: "management:customerSwitch.errorServiceComponentsUnknown",
  CUSTOMER_SWITCH_SITE_CONFLICT: "management:customerSwitch.errorSiteConflict",
  CUSTOMER_SWITCH_NO_BILLING: "management:customerSwitch.errorNoBilling",
  CUSTOMER_SWITCH_NO_SOURCE_PARAMETERS: "management:customerSwitch.errorNoSourceParameters",
  CUSTOMER_SWITCH_CONTRACT_CHANGED: "management:customerSwitch.errorContractChanged",
  CUSTOMER_SWITCH_FLEX_CONTRACT_CHANGED: "management:customerSwitch.errorFlexContractChanged",
  // The switch assertion fails closed: the frontend declared a Customer Switch but the
  // backend found no foreign rows, so it refuses instead of silently degrading to an
  // ordinary contract creation.
  CUSTOMER_SWITCH_NOT_FOREIGN: "management:customerSwitch.errorNotForeign",
  // A group member's company-B SDP could not be resolved after addSdps.
  CUSTOMER_SWITCH_MEMBER_SDP_MISSING: "management:customerSwitch.errorMemberSdpMissing",
  // Arrives on the SIGN path only: the pinned crossover date has already passed, so the
  // proposal can no longer be signed into effect. There is no regenerate path today —
  // the copy says who to contact rather than offering an action that does not exist.
  CUSTOMER_SWITCH_CROSSOVER_PASSED: "management:customerSwitch.errorCrossoverPassed",
  // Another company already has a pending (unsigned) switch on this EAN.
  CUSTOMER_SWITCH_PENDING: "management:customerSwitch.errorPending",
  // The three below are thrown INSIDE the sign transaction. They became reachable
  // only when that block started rethrowing the original coded error instead of
  // collapsing it to a boolean — before that they surfaced as untyped 404/500s,
  // which is why OWNER_CHANGED was once recorded as unreachable. Mapping them is
  // part of that same change, not a separate concern.
  CUSTOMER_SWITCH_OWNER_CHANGED: "management:customerSwitch.errorOwnerChanged",
  CUSTOMER_SWITCH_CROSSOVER_MISSING: "management:customerSwitch.errorCrossoverMissing",
  CUSTOMER_SWITCH_CROSSOVER_MISMATCH: "management:customerSwitch.errorCrossoverMismatch",
  // Group payload rejected by the backend's shape/consistency validation (400).
  INVALID_SWITCH_GROUP: "management:customerSwitch.errorInvalidSwitchGroup",
  // ContractPrerequisiteError subclasses — raised by getCertificatePrices / the partner fee
  // lookup, which BOTH contract generators also call, so these are deliberately not
  // CUSTOMER_SWITCH_* on the backend side.
  SDP_DIRECTION_NOT_ONBOARDED: "management:customerSwitch.errorSdpDirectionNotOnboarded",
  PARTNER_NOT_FOUND: "management:customerSwitch.errorPartnerNotFound",
};

export const CODES_WITH_SERVER_DETAIL = new Set([
  "CUSTOMER_SWITCH_FLEX_SIBLING_EAN",
  // The backend message names the UNRESOLVED DIRECTION ("...for the injection
  // direction: neither the landing company nor the current owner holds one"). Which
  // direction has no parameter source is precisely what tells the admin where to look,
  // and the localised copy cannot know it.
  "CUSTOMER_SWITCH_NO_SOURCE_PARAMETERS",
  // Names the member EAN whose SDP is missing — without it the admin cannot tell which
  // half of a two-point group failed.
  "CUSTOMER_SWITCH_MEMBER_SDP_MISSING",
  // Names the specific structural problem with the group payload.
  "INVALID_SWITCH_GROUP",
  // Names the two conflicting switch-over dates, which is the whole diagnostic.
  "CUSTOMER_SWITCH_CROSSOVER_MISMATCH",
]);

export const shouldShowServerDetail = (code?: string): boolean =>
  !!code && CODES_WITH_SERVER_DETAIL.has(code);

/**
 * The error envelope these endpoints can produce. Deliberately a UNION of both shapes in
 * play, because they are not the same across routes:
 *   - `contractOverview` / `addContract` (contractsHandler) → `{ error, code }`
 *   - the sign-token route (commoditySignTokenHandler)      → `{ message }` / `{ errors }`
 * A consumer that reads only one shape drops the other silently — which is precisely how a
 * 409 reason reaches a customer as "something went wrong".
 */
export interface ContractErrorBody {
  error?: string;
  code?: string;
  message?: string;
  errors?: Record<string, string>;
}

/**
 * Resolve a backend error body to display copy.
 *
 * Order is deliberate: a MAPPED code wins (localised), then the backend's own sentence
 * (English, but specific and true), then the caller's generic fallback. Returning the raw
 * sentence before the generic string is what keeps a newly-added backend code informative
 * instead of invisible while the map catches up.
 */
export const resolveContractErrorCopy = (
  body: ContractErrorBody | undefined,
  t: (key: string) => string,
  fallback: string
): { message: string; detail?: string } => {
  const key = body?.code ? CUSTOMER_SWITCH_ERROR_KEYS[body.code] : undefined;
  if (key) {
    return {
      message: t(key),
      detail: shouldShowServerDetail(body?.code)
        ? body?.error || body?.message || undefined
        : undefined,
    };
  }
  const firstFieldError = body?.errors ? Object.values(body.errors)[0] : undefined;
  return { message: firstFieldError || body?.error || body?.message || fallback };
};
